// React modules
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

// react router
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

// icons
import {
  BanknotesIcon,
  DocumentDuplicateIcon,
  MapIcon,
  ShieldCheckIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';

// images
import FallbackPersonImage from 'assets/fallback-person-image.png';
import NotificationBellIcon from 'assets/icons/Notif-bell-icon.svg';
import PlainBellIcon from 'assets/icons/Plain-bell-icon.svg';
import LogoutIcon from 'assets/icons/logout.svg';
import LogoutIcon_red from 'assets/icons/logoutred.svg';
import route_icon from 'assets/icons/route_icon.svg';
import route_blue_icon from 'assets/icons/routing_blue_icon.svg';
import logo from 'assets/logo_dashboard.svg';
import Image from 'src/shared/Image';

// redux
import { useSelector } from 'react-redux';

// firebase
import { onMessage } from 'firebase/messaging';

import { useHandleLogout } from 'src/utils/useHandleLogout';

// components
import { NotificationDropdown } from 'src/screens/NotificationsScreen/NotificationDropdown';
import Message from 'src/shared/Message';

// hooks
import { useTranslation } from 'react-i18next';

// others
import { toast } from 'react-toastify';
import { isSupportUser } from 'src/utils/isSupportUser.utils';
import { messaging } from '../../firebase';
import { useLazyGetMediaUrlQuery } from '../api/generic.apis';
import { PAGE_TITLES } from '../constants/page_titles';
import { ROUTES } from '../constants/routes';
import useLocalizeDocumentAttributes from '../i18n/useLocalizeDocumentAttributes';
import type { RootState } from '../redux/store';
import { useAppSelector } from '../redux/store';

interface SidebarMenuItemProps {
  Icon?: typeof MapIcon;
  imgs?: string;
  text: string;
  route: string;
  className?: string;
  onClick: () => void;
}

const SidebarMenuItem = ({ Icon, imgs, text, route, className, onClick }: SidebarMenuItemProps) => {
  const { pathname: currentRoute } = useLocation();
  const navigate = useNavigate();
  className = className ? className + ' mt-3' : 'mt-3';

  const checkActive = (route: string) => {
    if (route === '/') {
      return currentRoute === route;
    } else {
      return currentRoute.startsWith(route);
    }
  };

  const handleClick = () => {
    navigate(route);
    onClick();
  };

  return (
    <div className={className} onClick={handleClick}>
      <div
        className={`cursor-pointer flex gap-x-3 rounded-md p-3.5 px-8 group hover:bg-secondary-blue ${checkActive(route) ? 'bg-secondary-blue' : ''}`}
      >
        {Icon ? (
          <Icon
            className={`h-5 w-5 ${checkActive(route) ? 'text-primary-blue' : 'group-hover:text-woodsmoke text-woodsmoke'}`}
          />
        ) : checkActive(route) ? (
          <img src={route_blue_icon} alt="" className="h-5 w-5" />
        ) : (
          <img src={imgs} alt="" className="h-5 w-5" />
        )}
        <span
          className={`text-sm font-medium ${checkActive(route) ? 'text-primary-blue' : 'group-hover:text-woodsmoke text-woodsmoke'}`}
        >
          {text}
        </span>
      </div>
    </div>
  );
};

interface DashboardLayoutProps {
  children?: ReactNode; // Explicitly define children as a prop
}
export const DashboardLayout: React.FC<DashboardLayoutProps> = () => {
  const navigate = useNavigate();
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [getMediaUrl] = useLazyGetMediaUrlQuery();
  const { tokenExpired } = useAppSelector(store => store.authReducer);
  const username = useAppSelector(store => store.userReducer.username);
  const profilePictureKey = useAppSelector(store => store.userReducer.profilePictureKey);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [hasNotifications, setHasNotifications] = useState(false);
  const { handleLogout } = useHandleLogout();
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    onMessage(messaging, payload => {
      toast.warn(<Message notification={payload.notification} />);
      setHasNotifications(true);
    });
  }, []);

  useEffect(() => {
    if (tokenExpired) navigate(ROUTES.LOGIN);
  }, [tokenExpired, navigate]);

  useEffect(() => {
    if (!profilePictureKey) return;
    const sendGetProfilePictureRequest = async () => {
      const { data, error } = await getMediaUrl(profilePictureKey);

      if (data) {
        setProfilePicture(data.url);
      } else if (error) {
        console.error(error);
      }
    };

    sendGetProfilePictureRequest();
  }, [profilePictureKey]);

  const normalizePath = (path: string): string => {
    if (path === ROUTES.MAP_OVERVIEW) {
      return path;
    }
    return path.replace(/\/+$/, '');
  };

  const isMobileView = () => window.innerWidth <= 430;

  const getPageTitle = () => {
    const currentPath = normalizePath(window.location.pathname);
    const titles = PAGE_TITLES();

    if (!titles) {
      console.error('PAGE_TITLES is not defined or does not return an object.');
      return '';
    }

    let pageTitle = titles[currentPath];

    if (!pageTitle) {
      const routeKeys = Object.keys(titles);
      for (const route of routeKeys) {
        if (route.includes('/:')) {
          const routeRegex = new RegExp(`^${route.replace(/:[^/]+/g, '[^/]+')}$`);
          if (routeRegex.test(currentPath)) {
            pageTitle = titles[route];
            break;
          }
        }
      }
    }

    if (isMobileView() && pageTitle?.includes('Report')) {
      pageTitle = 'Reports';
    }

    if (!pageTitle) {
      console.log(`No page title found for route: ${currentPath}`);
    }

    return pageTitle || currentPath;
  };

  const handleBellClick = () => {
    setDropdownVisible(!dropdownVisible);
    if (dropdownVisible) {
      setHasNotifications(false);
    }
  };

  const handleNavigation = () => {
    setHasNotifications(false);
    navigate(ROUTES.NOTIFICATIONS);
  };

  const handleCloseDrawer = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="drawer lg:drawer-open">
      <input id="my-drawer-2" type="checkbox" className="drawer-toggle" checked={isSidebarOpen} readOnly />

      <div className="drawer-content">
        <div className="flex flex-col mt-5">
          <div className="navbar bg-base-100 w-full h-16 px-2 md:px-5">
            <div className="flex-1">
              <label
                htmlFor="my-drawer-2"
                className="btn btn-square btn-ghost lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  className="w-6 h-6 stroke-current"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              </label>
              <Link to="#">
                <h1 className="text-lg md:text-2xl font-bold">
                  {getPageTitle()
                    .split('/')
                    .map((segment, index, array) => (
                      <span key={index} className={index === array.length - 1 ? 'text-deep-blue' : 'text-muted'}>
                        {segment}
                        {index < array.length - 1 && ' / '}
                      </span>
                    ))}
                </h1>
              </Link>
            </div>

            <div className="flex items-center relative">
              <div className="relative hidden md:block">
                <img
                  src={hasNotifications ? NotificationBellIcon : PlainBellIcon}
                  alt="notification bell"
                  className=" cursor-pointer"
                  onClick={handleBellClick}
                />
                {dropdownVisible && (
                  <div className="absolute right-0 ml-5 w-80 bg-white z-50">
                    <NotificationDropdown
                      onClose={() => {
                        setDropdownVisible(false);
                        setHasNotifications(false);
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="relative md:hidden">
                <img
                  src={hasNotifications ? NotificationBellIcon : PlainBellIcon}
                  alt="notification bell"
                  className="mr-5 cursor-pointer"
                  onClick={handleNavigation}
                />
              </div>
            </div>
          </div>
          <div className="flex-1">
            <Outlet />
          </div>
        </div>
      </div>
      <div className={`drawer-side pr-32 md:pr-0 z-[10]  ${isSidebarOpen ? 'block' : 'hidden'} lg:block`}>
        <Sidebar
          username={username}
          onClose={handleCloseDrawer}
          handleLogout={handleLogout}
          profilePicture={profilePicture}
        />
      </div>
    </div>
  );
};
interface SidebarProps {
  username: string | null;
  onClose: () => void;
  handleLogout: () => void;
  profilePicture: string | null;
}
const Sidebar: React.FC<SidebarProps> = React.memo(({ username, onClose, handleLogout, profilePicture }) => {
  const isSuperUser = useSelector((state: RootState) => state.authReducer.isSuperUser);

  const { t } = useTranslation();
  const rtl = useLocalizeDocumentAttributes();
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  return (
    <div ref={rtl} className="menu h-full text-base-content border-r-2 px-5 z-1 flex flex-col flex-nowrap bg-white">
      <div>
        <img src={logo} alt="logo" className="md:m-8 hidden lg:block" />
        <div className="flex justify-between m-4 lg:hidden">
          <img src={logo} alt="logo" className="h-10" />
          <XMarkIcon className="h-5 w-5 cursor-pointer" onClick={onClose} />
        </div>

        {isSuperUser ? (
          <>
            <SidebarMenuItem Icon={UsersIcon} text={t('User Management')} route={ROUTES.ADMIN_USER} onClick={onClose} />
            <SidebarMenuItem
              Icon={BanknotesIcon}
              text={t('Device Management')}
              route={ROUTES.ADMIN_DEVICE}
              onClick={onClose}
            />
            <SidebarMenuItem
              Icon={TruckIcon}
              text={t('Vehicle Management')}
              route={ROUTES.ADMIN_VEHICLE}
              onClick={onClose}
            />
            <SidebarMenuItem
              Icon={UsersIcon}
              text={t('Organization Management')}
              route={ROUTES.ADMIN_ORGANIZATION}
              onClick={onClose}
            />
            <SidebarMenuItem
              Icon={UsersIcon}
              text={t('Release Version Management')}
              route={ROUTES.ADMIN_RELEASE_VERSION}
              onClick={onClose}
            />
          </>
        ) : (
          <>
            <SidebarMenuItem
              Icon={MapIcon}
              text={t('page_titles.fleet_view')}
              route={ROUTES.MAP_OVERVIEW}
              className="mt-0"
              onClick={onClose}
            />
            <SidebarMenuItem Icon={UsersIcon} text={t('driver_management')} route={ROUTES.DRIVERS} onClick={onClose} />
            <SidebarMenuItem
              Icon={TruckIcon}
              text={t('vehicle_management')}
              route={ROUTES.VEHICLES}
              onClick={onClose}
            />
            <SidebarMenuItem
              imgs={route_icon}
              text={t('routes')}
              route={ROUTES.ROUTES_OPTIMIZATION}
              onClick={onClose}
            />
            <SidebarMenuItem Icon={UserGroupIcon} text={t('groups')} route={ROUTES.GROUPS} onClick={onClose} />
            <SidebarMenuItem Icon={ShieldCheckIcon} text={t('safety')} route={ROUTES.SAFETY} onClick={onClose} />
            {isSupportUser() && (
              <SidebarMenuItem
                Icon={WrenchScrewdriverIcon}
                text={t('maintenance')}
                route={ROUTES.MAINTENANCE}
                onClick={onClose}
              />
            )}
            <SidebarMenuItem
              Icon={DocumentDuplicateIcon}
              text={t('reports')}
              route={ROUTES.REPORTS}
              onClick={onClose}
            />
          </>
        )}
      </div>

      <div className="mt-auto mb-10 py-4 px-5 bg-light-grey rounded-md">
        {username ? (
          <div
            onClick={() => navigate(ROUTES.PROFILE)}
            className="flex items-center gap-x-3 font-medium text-sm text-woodsmoke mt-2 mb-3 capitalize cursor-pointer hover:opacity-80 transition"
          >
            <div className="w-9 h-9 md:w-8 md:h-8 rounded-full overflow-hidden">
              <Image
                alt="User Profile Picture"
                src={profilePicture || FallbackPersonImage}
                className="object-cover w-full h-full"
              />
            </div>
            <span className="items-center">{username}</span>
          </div>
        ) : (
          <div></div>
        )}
        <hr className="h-px border-1 dark:bg-light-woodsmoke mb-2" />
        <div
          onClick={handleLogout}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          className="flex items-center gap-x-2 cursor-pointer py-1 px-3"
        >
          <img src={hover ? LogoutIcon_red : LogoutIcon} alt="logout" className="w-4 h-4" />
          <span className={`text-sm font-medium ${hover ? 'text-logout-red' : 'text-woodsmoke'}`}>{t('logout')}</span>
        </div>
      </div>
    </div>
  );
});
